<template>
    <div class="tw:space-y-4">
        <div class="tw:mb-4 tw:px-6">
            <h2 class="tw:text-sm tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wide tw:my-1">Sanciones</h2>
            <p class="tw:text-xs tw:font-semibold tw:text-gray-400 tw:my-1">Sanciones aplicadas al deportista</p>
        </div>

        <div class="tw:flex tw:justify-end tw:px-6">
            <Button
                @click="$emit('add-sancion')"
                class="tw:text-xs tw:sm:text-sm"
            >
                Añadir sanción
            </Button>
        </div>

        <div class="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:lg:grid-cols-4 tw:gap-3 tw:px-6 tw:mb-3">
            <BaseInputText
                label="Buscar"
                :value="local.search"
                name="sanciones-search"
                @input="onSearchInput"
            />

            <BaseMultiselect
                name="sanciones-estado"
                label="Estado"
                :options="pagadoOptions"
                value-key="id"
                label-key="name"
                :value="local.pagado"
                @input="val => local.pagado = (val ?? '')"
            />

            <BaseMultiselect
                name="sanciones-impuesta"
                label="Impuesta por"
                :options="impuestaOptions"
                value-key="id"
                label-key="name"
                :value="local.impuesta"
                @input="val => local.impuesta = (val ?? '')"
            />

            <BaseMultiselect
                name="sanciones-pagar"
                label="Pagado por"
                :options="pagarOptions"
                value-key="id"
                label-key="name"
                :value="local.pagar"
                @input="val => local.pagar = (val ?? '')"
            />
        </div>

        <SimpleTable
            :flat="true"
            id="sanciones"
            :columns="columns"
            :data="sanciones"
            :skeleton="!fetched || loading"
            :skeleton-rows="3"
            empty-text="No hay sanciones registradas"
            :default-col-width="defaultColWidth"
        >
            <!-- Skeleton headers -->
            <template #skeleton-header-fecha>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-16"></div>
            </template>
            <template #skeleton-header-sin_jugar_hasta>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-24"></div>
            </template>
            <template #skeleton-header-bloquear_acceso>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-24"></div>
            </template>
            <template #skeleton-header-motivo>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-16"></div>
            </template>
            <template #skeleton-header-acciones>
                <div class="tw:h-3 tw:bg-gray-200 tw:rounded tw:w-14"></div>
            </template>

            <!-- Skeleton rows -->
            <template #skeleton-fecha>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-40"></div>
            </template>
            <template #skeleton-sin_jugar_hasta>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-40"></div>
            </template>
            <template #skeleton-bloquear_acceso>
                <div class="tw:h-5 tw:w-5 tw:bg-gray-200 tw:rounded-full"></div>
            </template>
            <template #skeleton-motivo>
                <div class="tw:h-4 tw:bg-gray-200 tw:rounded tw:w-64"></div>
            </template>
            <template #skeleton-acciones>
                <div class="tw:h-8 tw:w-16 tw:bg-gray-200 tw:rounded"></div>
            </template>

            <template #cell-bloquear_acceso="{ row }">
                <span :class="row.bloquear_acceso ? 'tw:text-green-600' : 'tw:text-gray-400'">
                    {{ row.bloquear_acceso ? '✓' : '✗' }}
                </span>
            </template>

            <template #cell-motivo="{ row }">
                <div v-html="row.motivo" class="tw:text-gray-600"></div>
            </template>

            <template #cell-acciones="{ row }">
                <Button
                    @click="borrarSancion(row)"
                    variant="danger"
                    class="tw:text-xs tw:sm:text-sm"
                >
                    Borrar
                </Button>
            </template>
        </SimpleTable>
    </div>
</template>

<script setup>
    import { reactive, watch, onUnmounted } from 'vue'
    import Button from '@/modules/admin/ui/Button.vue'
    import SimpleTable from '@/modules/admin/ui/table/SimpleTable.vue'
    import BaseInputText from '@/modules/admin/ui/BaseInputText.vue'
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue'

    const props = defineProps({
        sanciones: {
            type: Array,
            default: () => []
        },
        base: {
            type: Object,
            required: true
        },
        fetched: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        defaultColWidth: {
            type: String,
            default: '140px'
        }
    })

    const emit = defineEmits(['borrar-sancion', 'add-sancion', 'update:sancionesSearch', 'update:sancionesFilter'])

    const local = reactive({
        search: '',
        pagado: null,
        impuesta: null,
        pagar: null
    })

    let searchTimer = null

    function onSearchInput(val) {
        local.search = val
        clearTimeout(searchTimer)
        searchTimer = setTimeout(() => {
            emit('update:sancionesSearch', local.search)
        }, 500)
    }

    watch(() => local.pagado, val => {
        emit('update:sancionesFilter', 'pagado', val)
    })

    watch(() => local.impuesta, val => {
        emit('update:sancionesFilter', 'impuesta', val)
    })

    watch(() => local.pagar, val => {
        emit('update:sancionesFilter', 'pagar', val)
    })

    onUnmounted(() => {
        clearTimeout(searchTimer)
    })

    const pagadoOptions = [
        { id: '1', name: 'Pagado' },
        { id: '0', name: 'No pagado' },
    ]

    const impuestaOptions = [
        { id: '0', name: 'Federación' },
        { id: '1', name: 'Club' },
        { id: '2', name: 'Cuerpo técnico' },
    ]

    const pagarOptions = [
        { id: '0', name: 'Club' },
        { id: '1', name: 'Deportista' },
    ]

    const columns = [
        { key: 'fecha', label: 'Fecha' },
        { key: 'sin_jugar_hasta', label: 'Sin jugar hasta' },
        { key: 'bloquear_acceso', label: 'Acceso bloqueado' },
        { key: 'motivo', label: 'Motivo' },
        { key: 'acciones', label: 'Acciones' }
    ]

    const borrarSancion = row => {
        emit('borrar-sancion', { sancionId: row.id, base: props.base })
    }
</script>
